import { useEffect, useState, useRef } from 'react';
import toast from 'react-hot-toast';
import { api } from '../lib/api';

interface CardFundPromotionModalProps {
  visible: boolean;
  onClose: () => void;
}

export default function CardFundPromotionModal({ visible, onClose }: CardFundPromotionModalProps) {
  const [fundPurchasedCount, setFundPurchasedCount] = useState(0);
  const [fundMaxCount, setFundMaxCount] = useState(3);
  const [endDate, setEndDate] = useState('');
  const [inPeriod, setInPeriod] = useState(false);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [showPayChannel, setShowPayChannel] = useState(false);
  const [payLoading, setPayLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const countdownRef = useRef<ReturnType<typeof setInterval>>();

  useEffect(() => {
    if (!visible) return;
    api.get('/card-password-setup/status').then(({ data }) => {
      setFundPurchasedCount(data.fund_purchased_count);
      setFundMaxCount(data.fund_max_count);
      setEndDate(data.end_date);
      const now = Date.now();
      const start = new Date(data.start_date.replace(' ', 'T') + '+08:00').getTime();
      const end = new Date(data.end_date.replace(' ', 'T') + '+08:00').getTime();
      setInPeriod(now >= start && now <= end);
      setLoaded(true);
    }).catch(() => {
      toast.error('加载失败');
    });
  }, [visible]);

  useEffect(() => {
    if (!endDate) return;
    const end = new Date(endDate.replace(' ', 'T') + '+08:00');
    const tick = () => {
      const diff = end.getTime() - Date.now();
      if (diff <= 0) {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        if (countdownRef.current) clearInterval(countdownRef.current);
        return;
      }
      setCountdown({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      });
    };
    tick();
    countdownRef.current = setInterval(tick, 1000);
    return () => { if (countdownRef.current) clearInterval(countdownRef.current); };
  }, [endDate]);

  async function handleFundPurchase(product: string) {
    if (payLoading) return;
    setPayLoading(true);
    try {
      const { data } = await api.post('/card-fund-promotion/purchase', { product });
      const payUrl = data?.data?.pay_url;
      if (payUrl) {
        window.location.href = payUrl;
      } else {
        toast.error('支付链接获取失败');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? '支付创建失败');
    } finally {
      setPayLoading(false);
      setShowPayChannel(false);
    }
  }

  function handleClose() {
    if (payLoading) return;
    setShowPayChannel(false);
    onClose();
  }

  if (!visible || (loaded && !inPeriod)) return null;

  if (showPayChannel) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={handleClose}>
        <div className="bg-white rounded-2xl w-[85%] max-w-sm p-5" onClick={e => e.stopPropagation()}>
          <h3 className="text-center text-base font-bold mb-1">红色初心·党员钱包基金</h3>
          <p className="text-center text-xs text-gray-500 mb-4">金额: ¥368.00</p>
          <div className="space-y-3">
            <button
              onClick={() => handleFundPurchase('WX')}
              disabled={payLoading}
              className="w-full py-3 rounded-xl text-base font-medium flex items-center justify-center gap-2 disabled:opacity-60"
              style={{ backgroundColor: '#07c160', color: '#fff' }}
            >
              {payLoading ? '处理中...' : '微信支付'}
            </button>
            <button
              onClick={() => handleFundPurchase('ALI')}
              disabled={payLoading}
              className="w-full py-3 rounded-xl text-base font-medium flex items-center justify-center gap-2 disabled:opacity-60"
              style={{ backgroundColor: '#1677ff', color: '#fff' }}
            >
              {payLoading ? '处理中...' : '支付宝'}
            </button>
            <button
              onClick={() => handleFundPurchase('ALI2')}
              disabled={payLoading}
              className="w-full py-3 rounded-xl text-base font-medium flex items-center justify-center gap-2 disabled:opacity-60"
              style={{ backgroundColor: '#1677ff', color: '#fff' }}
            >
              {payLoading ? '处理中...' : '支付宝2'}
            </button>
          </div>
          <button
            onClick={handleClose}
            disabled={payLoading}
            className="w-full mt-3 py-2 text-sm text-gray-400"
          >
            取消
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={handleClose}>
      <div className="w-[330px] max-w-md relative" onClick={e => e.stopPropagation()}>
        <button
          onClick={handleClose}
          className="absolute -top-3 -right-3 z-10 w-8 h-8 rounded-full bg-white text-gray-500 flex items-center justify-center shadow-lg text-lg font-bold"
        >&times;</button>

        <div className="relative">
          <img src="/images/cardjijin_beijing.png" alt="" className="w-full rounded-xl" />

          {loaded && (
            <div className="relative w-full">
              <div className="absolute bottom-[63px] left-[55px] flex items-start justify-between">
                <div className="flex flex-col items-start mt-[5px]">
                  <span className="text-[10px] text-[#8b0000]">每人限购{fundMaxCount}份，当前购入
                    <span className="text-[14px] font-bold text-[#c41a1a]">{fundPurchasedCount}/{fundMaxCount}</span></span>
                </div>
              </div>
              <div className="absolute bottom-[55px] -right-[40px] px-5 flex items-start justify-between">
                <div className="flex flex-col items-start gap-0 -mt-[0px] mr-[40px]">
                  <span className="text-[11px] text-[#8b0000]">密码设置倒计时</span>
                  <span className="text-[12px] font-bold text-[#c41a1a]">
                    {countdown.days}天{String(countdown.hours).padStart(2, '0')}时{String(countdown.minutes).padStart(2, '0')}分{String(countdown.seconds).padStart(2, '0')}秒
                  </span>
                </div>
              </div>

              <div className="absolute bottom-[18px] left-0 right-0 flex justify-center px-6">
                <button
                  onClick={() => setShowPayChannel(true)}
                  disabled={fundPurchasedCount >= fundMaxCount}
                  className="disabled:opacity-60"
                >
                  <img src="/images/cardjijin_liji.png" alt="立即办理" className="h-[34px]" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
